/**
 * Бар действий на медиа-плитке: «развернуть» (полноразмерный просмотр) и, если
 * у плитки есть ролик со звуком, — динамик. ОДНА матовая пилюля на все витрины
 * (лента трендов, витрина фотостудии/трендов, «Сценарий оживления»): раньше в
 * каждом месте лежал свой «глазик» со своими стилями (владелец 15.09: общепринятая
 * иконка разворота, звук в той же зоне, матовая подложка с блюром и тенью).
 *
 * Разметка — span[role=button], не <button>: плитки сами бывают ссылкой или
 * кнопкой, а кнопка внутри кнопки — невалидна и ломает клавиатуру.
 *
 * Показ/скрытие бара — дело хозяина (на наведении у ленты, всегда — в лаборатории):
 * стили пилюли здесь, правило появления — в стилях хозяина через миксин reveal.
 */
import type { KeyboardEvent, MouseEvent } from 'react'

import { Icon } from '../../public/icons/Icon.tsx'

export interface CardMediaBarProps {
	/** Полноразмерный просмотр (бывший «глазик»). Нет — кнопки нет. */
	onExpand?: () => void
	expandLabel?: string
	/** Скачать файл — только там, где он принадлежит человеку (результаты). */
	onDownload?: () => void
	/** Есть — рисуем динамик. `on` — звук сейчас включён. */
	sound?: { on: boolean; onToggle: () => void }
	/** Угол плитки; по умолчанию правый верхний. */
	corner?: 'tr' | 'tl'
	/** 'sm' — компактный бар для мелких плиток (кнопки 20px вместо 24). */
	size?: 'md' | 'sm'
	className?: string
}

export default function CardMediaBar({
	onExpand,
	expandLabel = 'Смотреть в полном размере',
	onDownload,
	sound,
	corner = 'tr',
	size = 'md',
	className = '',
}: CardMediaBarProps) {
	// Клик по бару — не клик по плитке: плитка открыла бы инструмент.
	const stop = (e: MouseEvent | KeyboardEvent) => {
		e.stopPropagation()
		e.preventDefault()
	}
	const act = (fn: () => void) => ({
		onClick: (e: MouseEvent) => {
			stop(e)
			fn()
		},
		onKeyDown: (e: KeyboardEvent) => {
			if (e.key === 'Enter' || e.key === ' ') {
				stop(e)
				fn()
			}
		},
	})
	const soundLabel = sound?.on ? 'Выключить звук' : 'Включить звук'
	return (
		<span
			className={`cmb cmb--${corner}${size === 'sm' ? ' cmb--sm' : ''}${className ? ` ${className}` : ''}`}
			role="group"
			aria-label="Действия с медиа"
			// Наведение на бар не должно перезапускать ролик плитки (у неё свой
			// onMouseEnter «смотреть с начала»).
			onMouseEnter={(e) => e.stopPropagation()}
		>
			{sound && (
				<span
					className={`cmb__btn${sound.on ? ' cmb__btn--on' : ''}`}
					role="button"
					tabIndex={0}
					aria-label={soundLabel}
					aria-pressed={sound.on}
					title={soundLabel}
					{...act(sound.onToggle)}
				>
					<Icon id={sound.on ? 'gen-audio' : 'gen-audio-off'} size={13} />
				</span>
			)}
			{onExpand && (
				<span
					className="cmb__btn"
					role="button"
					tabIndex={0}
					aria-label={expandLabel}
					title={expandLabel}
					{...act(onExpand)}
				>
					<Icon id="gen-expand" size={12} />
				</span>
			)}
			{onDownload && (
				<span
					className="cmb__btn"
					role="button"
					tabIndex={0}
					aria-label="Скачать"
					title="Скачать"
					{...act(onDownload)}
				>
					<Icon id="gen-download" size={13} />
				</span>
			)}
		</span>
	)
}
